/**
 * Call-clock arithmetic, kept pure and framework-free so the surface, the island
 * and the tests all read the same numbers.
 *
 * Two things the user could not see before: how long they have been talking, and
 * that the call is about to be cut off. The cap is real — `lib/voice-quota.ts`
 * refuses to re-fund a call past `MAX_CALL_DURATION_MS`, and the mint response
 * carries that ceiling back to every client (web, iOS, Android) as
 * `maxCallDurationMs`. Until now a long call simply stopped.
 */

/** How close to the cap the surface starts saying so. */
export const ENDING_SOON_MS = 120_000;

/**
 * `m:ss`, or `h:mm:ss` once the call passes an hour. Deliberately not
 * `Intl.DurationFormat` — this is a running clock, and it must be identical to
 * the two native formatters (`VoiceCallText.elapsedLabel` / `VoiceCallFormat.elapsed`).
 */
export function formatElapsed(elapsedMs: number): string {
  const total = Math.max(0, Math.floor(elapsedMs / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * Whole minutes left before the cap, or `null` when there is nothing to warn
 * about (no cap known, or still comfortably inside it).
 *
 * Never returns 0 or a negative: at or past the ceiling the honest thing to say
 * is still "about a minute", not "0 min" — the call is ending either way, and a
 * zero reads as a bug.
 */
export function endingSoonMinutes(elapsedMs: number, maxCallDurationMs: number | null): number | null {
  if (!maxCallDurationMs || maxCallDurationMs <= 0) return null;
  const remaining = maxCallDurationMs - elapsedMs;
  if (remaining > ENDING_SOON_MS) return null;
  return Math.max(1, Math.ceil(remaining / 60_000));
}
